import {AfterViewInit, ChangeDetectorRef, Component, inject, model, OnInit, signal, ViewChild} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {NgOptimizedImage} from "@angular/common";
import {assets} from '@core/constants';
import {Coding} from '@core/classes/other/coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {ATC} from '@core/classes/other/atc';
import {HttpHeaders} from '@angular/common/http';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {lastValueFrom} from 'rxjs';
import {Response} from '@core/classes/other/response';
import moment from 'moment/moment';
import {checkDate} from '@core/helpers';

@Component({
  selector: 'ncp-add-diagnostic-test',
  imports: [
    ButtonComponent,
    DateComponent,
    DropdownComponent,
    FormsModule,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule
  ],
  templateUrl: './add-diagnostic-test.component.html',
  styleUrl: './add-diagnostic-test.component.scss',
})
export class AddDiagnosticTestComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('dtFrm') protected diagnosticTestFormEl?: NgForm;
  @ViewChild('addDiagnosticTestButton') public addDiagnosticTestButton!: ButtonComponent;

  @ViewChild( 'bloodTypesDesc') public bloodTypesDesc!: DropdownComponent<Coding>;
  @ViewChild('testDate') public testDate!: DateComponent;

  @ViewChild('addDiagnosticTestButton') getSPbtn  = () => {
    return this.addDiagnosticTestButton
  }

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | undefined>(undefined, [Validators.required]),
    date: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let codingId = (control as FormGroup).controls['coding_id'];
      let date = (control as FormGroup).controls['date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({dt_codingIdInvalid: true});
        errors['dt_codingIdInvalid'] = true;
      }

      if(!checkDate(date.value).isValid()) {
        date.setErrors({dt_dateFormatInvalid: true});
        errors['dt_dateInvalid'] = true;
      } else {
        if(checkDate(date.value).isBefore(patientDOB)) {
          date.setErrors({da_onSetDateAfterDOB: true});
          errors['dt_dateAfterDOB'] = true;
        } else if(checkDate(date.value).isSameOrAfter(today)) {
          date.setErrors({da_onSetDateBeforeToday: true});
          errors['dt_dateBeforeToday'] = true;
        } else {
          date.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  protected dtLoading: boolean = false;

  public btnDTOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'dtBtn',
      tooltip: 'add_diagnostic_test',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_diagnostic_test',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected bloodTypesDescDDOptions = signal<DropdownOptions<Coding | ATC>>(
    new DropdownOptions<Coding | ATC>({
      id: 'bloodTypesDescription',
      name: 'bloodTypesDescription',
      placeholder: 'blood_type',
      label: {
        id: "blood_type_desc_label",
        text: 'blood_type',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.20',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('coding_id') as FormControl
    })
  );

  protected testDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'testDate',
      label: {
        text: 'test_date',
      },
      name: 'testDate',
      initialValue: '',
      formControl: this.form.get('date') as FormControl,
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  ngOnInit() {
    this.btnDTOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      this.bloodTypesDesc.detectChanges();
      this.addDiagnosticTestButton.blur();
      this.dtLoading = true;
      this.downloadDropDownData(this.mod.options().title).then((value) => {
        this.dtLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    //this.testDateOptions().formControl = (this.form.get('dt') as FormControl);
    this.modOptions().onSave = this.onSave;
    this.modOptions().onShow = this.downloadDropDownData;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.bloodTypesDesc.clearSelection();
    this.testDate.reset()

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  protected downloadDropDownData = async (title?: string) => {
    let ddData = JSON.parse(localStorage.getItem('dropDownData')!) as Object || {};

    if(ddData.hasOwnProperty('bloodTypesDescription')) {
      return;
    }

    const data = await lastValueFrom(this.apiService.call({
      path: '/get-all_codings',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      body: {
        oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.20',
        description: ''
      }
    })!);

    if (data && data.status === 1 && data.data.length > 0) {
      Object.defineProperty(ddData, 'bloodTypesDescription', {
        value: (data as Response<Coding>).data,
        enumerable: true,
        writable: false
      });
      this.bloodTypesDesc.data = (data as Response<Coding>).data || [];

      localStorage.setItem('dropDownData', JSON.stringify(ddData));
    }
  }

  onSave = () => {
    this.diagnosticTestFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    console.log(this.form.value);
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      dt: (this.form.get('date') as FormControl).value,
      coding_id: this.bloodTypesDescDDOptions().value!.coding_id,
      description: this.bloodTypesDescDDOptions().value!.description,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/diagnostic-test',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe(res => {
      if(res && res.status === 1) {
        let data = this.data()?.tblOptions().data
        body = {
          ...body,
          ...{
            description: this.bloodTypesDescDDOptions().value!.description,
          }
        };
        data.unshift(body);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        //this.data()?.tbl?.detectChanges();
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
